import { c as _c } from "react/compiler-runtime";
import { TableCssVariables } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { useMutation, UseMutationResult, UseMutationOptions } from '@tanstack/react-query';
import { useRef } from 'react';
export type UseAbortableMutationOptions<TData, TError, TVariables, TContext = unknown> = Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> & {
  mutationFn: (v: TVariables, signal: AbortSignal) => Promise<TData>;
  debounce?: number;
};
export const useDebounceMutation = t0 => {
  const $ = _c(27);
  let mutationFn;
  let onError;
  let onMutate;
  let onSettled;
  let onSuccess;
  let options;
  let t1;
  if ($[0] !== t0) {
    ({
      mutationFn,
      onSuccess,
      onError,
      onMutate,
      onSettled,
      debounce: t1,
      ...options
    } = t0);
    $[0] = t0;
    $[1] = mutationFn;
    $[2] = onError;
    $[3] = onMutate;
    $[4] = onSettled;
    $[5] = onSuccess;
    $[6] = options;
    $[7] = t1;
  } else {
    mutationFn = $[1];
    onError = $[2];
    onMutate = $[3];
    onSettled = $[4];
    onSuccess = $[5];
    options = $[6];
    t1 = $[7];
  }
  const debounce = t1 === undefined ? 800 : t1;
  const abortControllerRef = useRef(null);
  let t2;
  if ($[8] !== mutationFn) {
    t2 = async variables => {
      abortControllerRef.current?.abort("new-request");
      const controller = new AbortController();
      abortControllerRef.current = controller;
      return await mutationFn(variables, controller.signal);
    };
    $[8] = mutationFn;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== onSuccess) {
    t3 = (data, variables_0, onMutateResult, context) => {
      if (!abortControllerRef.current?.signal.aborted) {
        onSuccess?.(data, variables_0, onMutateResult, context);
      }
    };
    $[10] = onSuccess;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== onError || $[13] !== onMutate || $[14] !== onSettled || $[15] !== options || $[16] !== t2 || $[17] !== t3) {
    t4 = {
      ...options,
      mutationFn: t2,
      onSuccess: t3,
      onError,
      onMutate,
      onSettled
    };
    $[12] = onError;
    $[13] = onMutate;
    $[14] = onSettled;
    $[15] = options;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const mutation = useMutation(t4);
  let t5;
  if ($[19] !== mutation) {
    t5 = () => {
      abortControllerRef.current?.abort("user-cancelled");
      abortControllerRef.current = null;
      mutation.reset();
    };
    $[19] = mutation;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const cancel = t5;
  let t6;
  if ($[21] !== mutation) {
    t6 = args => mutation.mutate(args);
    $[21] = mutation;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const debouncedMutate = useDebouncedCallback(t6, debounce);
  let t7;
  if ($[23] !== cancel || $[24] !== debouncedMutate || $[25] !== mutation) {
    t7 = {
      cancel,
      debouncedMutate,
      ...mutation
    };
    $[23] = cancel;
    $[24] = debouncedMutate;
    $[25] = mutation;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7 as UseMutationResult<TData, TError, TVariables, TContext> & {
    cancel: () => void;
    debouncedMutate: (args: TVariables) => void;
  };
};